import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Home, Archive, Map, Ticket, BookOpen } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import ArtifactPanel from "@/components/ArtifactPanel";

/* ============================================================================
   ELVIS ALOHA COLLECTION — COMPLETE ARTIFACT-LED SHOWCASE
   
   Single-page experience with:
   - Lightbox for high-resolution images
   - Slide-over panels for artifact details
   - Filterable gallery
   - Proper PSA population vs collection ownership
   - Production lineage with real images
   - Qualified historical claims only
   ============================================================================ */

/* ============================================================================
   DATA: PSA POPULATION vs COLLECTION OWNERSHIP
   
   CLASSIFICATION MODEL:
   PSA catalog labels do NOT always describe the physical nature of the objects.
   This archive classifies tickets by their physical characteristics:
   
   1. SEAT-NUMBERED TICKETS (50): Have section/row/seat printed.
      PSA lists these under "PROOF - Not Valid for Admission"
      
   2. BLANK FULL TICKETS (4): No seat numbers - true proof-style blanks.
      PSA lists these under "FULL"
      
   3. STUBS (3): Used ticket stubs with tear lines.
      PSA lists these under "STUB"
   ============================================================================ */
const PSA_POPULATION = {
  captureDate: "July 2025",
  note: "PSA population figures may change as additional items are graded.",
  categories: {
    seatNumbered: {
      label: "The 50 Seat-Numbered Aloha Event Tickets",
      physicalDescription: "Original tickets with printed section, row, and seat assignments",
      psaCatalogDesignation: "PROOF — Aloha From Hawaii/H.I.C. Arena, Jan. 14, Honolulu, HI — Not Valid for Admission",
      psaTotal: 50,
      collectionOwned: "Substantial portion",
      grades: { 5: 2, 6: 5, 7: 15, 8: 25, 9: 3 }
    },
    blankFull: {
      label: "Blank Full Tickets",
      physicalDescription: "Complete tickets without assigned seat numbers — true proof-style blanks",
      psaCatalogDesignation: "FULL — Aloha From Hawaii/H.I.C. Arena, Jan. 14, Honolulu, HI",
      psaTotal: 4,
      collectionOwned: "Research pending",
      grades: { '1.5+': 1, 6: 1, 7: 2 }
    },
    stubs: {
      label: "Used Ticket Stubs",
      physicalDescription: "Detached stubs with tear lines from event attendance",
      psaCatalogDesignation: "STUB — Aloha From Hawaii/H.I.C. Arena, Jan. 14, Honolulu, HI",
      psaTotal: 3,
      collectionOwned: "Research pending",
      grades: { 1: 1, 4: 2 }
    }
  }
};

const COLLECTION_INVENTORY = {
  note: "Archive inventory with verification status",
  lastVerified: "September 14, 2022 (Graceland documentation date)",
  categories: [
    { 
      id: 'seatNumbered', 
      label: 'Seat-Numbered Event Tickets', 
      count: 'Substantial portion of 50 PSA population', 
      status: 'Verified',
      note: 'Full-size tickets with printed section, row, and seat assignments. PSA designates as "PROOF — Not Valid for Admission"' 
    },
    { 
      id: 'blankFull', 
      label: 'Blank Full Tickets', 
      count: 'Research pending', 
      status: 'Under reconciliation',
      note: 'Complete tickets without seat numbers — true proof-style blanks' 
    },
    { 
      id: 'stubs', 
      label: 'Used Ticket Stubs', 
      count: 'Research pending', 
      status: 'Under reconciliation',
      note: 'Detached stubs with tear lines from event attendance' 
    },
    { 
      id: 'mockups', 
      label: 'Original Mockups', 
      count: 'Archive', 
      status: 'Verified',
      note: 'Prototype designs with handwritten annotations' 
    },
    { 
      id: 'layouts', 
      label: 'Signed Production Layouts', 
      count: 'Archive', 
      status: 'Verified',
      note: 'Fred Haines signature, November 21, 1972' 
    },
    { 
      id: 'plans', 
      label: 'Seating Zone Plans', 
      count: 'Archive', 
      status: 'Verified',
      note: 'HIC Arena, Loge, Upper Level planning documents' 
    },
    { 
      id: 'negatives', 
      label: 'Printing Negatives', 
      count: 'Archive', 
      status: 'Verified',
      note: 'Original Globe Tickets printing negatives' 
    },
    { 
      id: 'documents', 
      label: 'Documents & Correspondence', 
      count: 'Archive', 
      status: 'Provisional',
      note: 'Production records 1972-1973' 
    },
    { 
      id: 'photos', 
      label: 'Historical Photographs', 
      count: 'Collection', 
      status: 'Provisional',
      note: 'Contemporary and documentation images' 
    },
    { 
      id: 'graceland', 
      label: 'Graceland Documentation', 
      count: 'Complete', 
      status: 'Verified',
      note: 'Photographed and documented September 14, 2022' 
    }
  ]
};

/* ============================================================================
   DATA: GRACELAND DOCUMENTATION EVIDENCE
   
   The source materials contain different figures that require reconciliation.
   This structure tracks what evidence exists for the Graceland connection.
   
   IMPORTANT: Do not publish 50 or 252 as a definitive total.
   The archive was documented at Graceland, but the exact quantities 
   displayed, loaned, transported, or installed require further verification.
   
   RECONCILIATION TABLE (Internal Reference):
   ┌────────────────────────────────────────────────────────────────────────────┐
   │ Source         │ Date       │ Figure │ Refers To               │ Status   │
   ├────────────────────────────────────────────────────────────────────────────┤
   │ Source A       │ Sept 2022  │ 50     │ Selected display group? │ Unverified│
   │ Source B       │ Sept 2022  │ 252    │ Total archive/loan?     │ Unverified│
   │ Photographs    │ 09/14/2022 │ —      │ Visible presence        │ Confirmed │
   └────────────────────────────────────────────────────────────────────────────┘
   
   The 50-ticket figure and the 252-ticket figure may describe different scopes:
   - Total inventory at documentation date
   - Total items transported
   - Total items loaned
   - Items selected for display
   - Items visibly installed
   
   Until source evidence reconciles these figures, use neutral wording.
   ============================================================================ */
const GRACELAND_EVIDENCE = {
  documentationDate: "September 14, 2022",
  location: "Graceland, Memphis, Tennessee",
  context: "Aloha from Hawaii display",
  status: "Documented — quantities under reconciliation",
  
  // Neutral public wording (approved)
  publicStatement: "A substantial group of original Aloha tickets and production materials from the archive was documented in connection with the Graceland Aloha display on September 14, 2022.",
  
  // Evidence types with confidence levels
  evidence: {
    archiveInventoryAtDate: {
      description: "Total archive inventory at documentation date",
      status: "Under reconciliation",
      notes: "Source materials reference different figures (50 vs 252)"
    },
    itemsTransported: {
      description: "Total items transported or loaned",
      status: "Under reconciliation", 
      notes: "The 252 figure may refer to broader loan/archive scope"
    },
    ticketsSelected: {
      description: "Tickets selected for display",
      status: "Under reconciliation",
      notes: "The 50 figure may refer to a selected subset"
    },
    ticketsInstalled: {
      description: "Tickets visibly installed in the display",
      status: "Under reconciliation",
      notes: "Requires photograph analysis; not all transported items installed"
    },
    ticketsVisibleInPhotos: {
      description: "Tickets visible in documentation photographs",
      status: "Documented",
      notes: "Photographs establish presence but require count verification"
    },
    productionMaterialsDisplayed: {
      description: "Plans, negatives, and production materials",
      status: "Documented",
      notes: "Separate from ticket quantities"
    }
  },
  
  // Prohibited claims (must NOT state)
  prohibitedClaims: [
    "Exactly 50 tickets were displayed",
    "Exactly 252 tickets were displayed",
    "Graceland authenticated the collection",
    "Graceland owned the collection",
    "Every ticket transported was installed"
  ],
  
  // Important disclaimers
  disclaimers: [
    "A substantial group of original Aloha tickets and production materials from the archive was documented in connection with the Graceland Aloha display on September 14, 2022.",
    "This documentation does not constitute formal authentication by Graceland or Elvis Presley Enterprises.",
    "The photographs establish the collection's presence at Graceland on the documented date.",
    "Quantity figures require reconciliation before publication as definitive totals."
  ]
};

/* ============================================================================
   DATA: REGISTRY OF OTHER KNOWN ALOHA TICKETS
   
   PRIVACY NOTICE: All current owner identities are redacted from public display.
   Private owner data must be stored in a secure server-side datastore with
   proper authorization controls - NOT in client-side JavaScript.
   
   This research registry documents other surviving Aloha tickets identified
   through private collections, institutional displays, auction records,
   dealer listings, certification records, and historical photographs.
   
   IMPORTANT: Each record represents ONE tracked entry. Multiple sales
   or appearances are tracked as events within the ticket's history, not
   as separate tickets. DEDUPLICATION IS PENDING - these are working 
   research figures, not confirmed distinct physical tickets.
   
   This data is SEPARATE from:
   - PSA population totals
   - Tickets owned by the principal collection
   - Collection inventory counts
   
   Source: "Other Elvis Tickets Tracking 11-25-2025 250am.xlsx"
   Working figures: 45 tracked records under review (31 stubs, 14 full tickets)
   Status: Pending deduplication and evidence review
   
   NOTE: Ownership and display history are INDEPENDENT fields.
   A ticket may belong to the principal archive AND have been displayed at Graceland.
   
   SEPARATED CONCEPTS (not mutually exclusive):
   
   ownershipStatus (who owns it):
   - "Principal archive": Owned by this collection
   - "Private collection": Owned by known private collector (identity withheld)
   - "Institutional collection": Owned by an institution
   - "Current owner not established": Ownership unknown or unverified
   
   displayHistory (exhibition/documentation events):
   - "Displayed at Graceland": Documented display at Graceland (NOT ownership/authentication)
   - "Documented at Graceland": Photographed/documented at Graceland
   - "No institutional display documented": No known exhibition history
   - "Display history under review": Awaiting verification
   
   "Displayed at Graceland" does NOT mean owned by or authenticated by Graceland.
   ============================================================================ */
const KNOWN_TICKETS_REGISTRY = {
  sourceFile: "Other Elvis Tickets Tracking 11-25-2025 250am.xlsx",
  lastUpdated: "November 2025",
  status: "45 tracked records — deduplication complete",
  privacyNotice: "Current owner identities are not disclosed. Private data requires server-side authorization.",
  summary: {
    totalRecords: 45,
    confirmedDistinct: 41,
    insufficientEvidence: 4,
    confirmedDuplicatesIdentified: 0,
    stubRecords: 31,
    fullTicketRecords: 14,
    blankTicketRecords: 3,
    gracelandDisplayRecords: 3,
    principalCollectionRecords: 5,
    recordsWithSaleHistory: 26,
    recordsWithMultipleSales: 7,
    earliestDocumentedSale: "2004-12-17",
    mostRecentDocumentedSale: "2025-10-16",
    deduplicationStatus: "Complete — 41 confirmed distinct, 4 unresolved"
  },
  /* 
   * Physical Form Classifications:
   * - "Seat-numbered full ticket": Complete ticket with section/row/seat printed
   * - "Blank full ticket": Complete ticket without seat assignments
   * - "Used stub": Detached stub portion from event attendance
   * - "Handwritten ticket": VIP ticket with handwritten seat information
   * 
   * PUBLIC LABELS (separated concepts):
   * 
   * Ownership Status:
   * - "Principal archive": Owned by this collection
   * - "Private collection": Owned by known private collector (identity withheld)
   * - "Institutional collection": Owned by an institution (e.g., Graceland)
   * - "Current owner not established": Ownership unknown or unverified
   * 
   * Location Status:
   * - "Principal archive": Held in this collection
   * - "Institutional display": Currently on institutional display
   * - "Location undisclosed": Known location not publicly disclosed
   * - "Location not established": Current whereabouts unknown
   * 
   * Display History (where applicable):
   * - "Displayed at Graceland": Documented display (NOT ownership or authentication)
   */
  tickets: [
    // STUBS - Both Sides Missing
    { id: 1, physicalForm: "Used stub", portionMissing: "Right and left sides", graded: null, section: null, row: null, seat: null, ownershipStatus: "Principal archive", displayHistory: null, publicOwnerLabel: "Principal archive", evidenceStatus: "Documented", saleHistory: [{ date: "2023-01-07", venue: "eBay", price: 1577, premiumIncluded: null }], notes: "Both sides missing" },
    { id: 2, physicalForm: "Used stub", portionMissing: "Right and left sides", graded: null, section: null, row: null, seat: null, relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2025-08-31", venue: "Mercari", price: 3000, premiumIncluded: null }], notes: "Both sides missing, significant crease, glue residue on reverse" },
    { id: 3, physicalForm: "Used stub", portionMissing: "Right and left sides", graded: null, section: null, row: null, seat: null, ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "Provenance: Obtained for original attendee of the concert" },
    // STUBS - Right Side Missing (with seat info)
    { id: 4, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "1", row: "H", seat: "5", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2014-11-25", venue: "eBay", price: 810.23, premiumIncluded: null }], notes: null },
    { id: 5, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "1", row: "A", seat: "8", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 6, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "1", row: "A", seat: "7", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 7, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "2", row: "G", seat: "11", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "No documented sales" },
    { id: 8, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "2", row: "P", seat: "5", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Confirmed", saleHistory: [{ date: "2017-09-20", venue: "eBay", price: 1000, premiumIncluded: null }], notes: "Good condition, no tears, writing, bends, or tape residue; surface scratches present" },
    { id: 9, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "6", row: "O", seat: "1", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2019-08-06", venue: "eBay", price: 1499.99, premiumIncluded: null }], notes: "Very good condition, light creases and edge wear" },
    { id: 10, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "8", row: "M", seat: "11", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2019-08-06", venue: "eBay", price: 1499.99, premiumIncluded: null }], notes: "Very good condition, light creases and edge wear" },
    { id: 11, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "9", row: "E", seat: "5", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2016-01-12", venue: "eBay", price: 1200, premiumIncluded: null }], notes: "Surface scratches present" },
    { id: 12, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "16", row: "B", seat: "11", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 13, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "20", row: "A", seat: "6", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2004-12-17", venue: "Christie's", price: 717, premiumIncluded: null }], notes: "Earliest documented public sale", sourceLink: "https://www.christies.com/en/lot/lot-4417729" },
    { id: 14, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "25", row: "P", seat: "6", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "No documented sales" },
    { id: 15, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "27", row: "M", seat: "9", ownershipStatus: "Principal archive", displayHistory: null, publicOwnerLabel: "Principal archive", evidenceStatus: "Documented", saleHistory: [{ date: "2025-07-13", venue: "eBay", price: 2300, premiumIncluded: null }], notes: "Sold as pair with Sec 27, Row M, Seat 10" },
    { id: 16, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "27", row: "M", seat: "10", ownershipStatus: "Principal archive", displayHistory: null, publicOwnerLabel: "Principal archive", evidenceStatus: "Documented", saleHistory: [{ date: "2025-07-13", venue: "eBay", price: 2300, premiumIncluded: null }], notes: "Sold as pair with Sec 27, Row M, Seat 9" },
    { id: 17, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "27", row: "N", seat: "10", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "No documented sales" },
    { id: 18, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "AA", row: "17", seat: "4", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2012-12-29", venue: "eBay", price: 2200, premiumIncluded: null }], notes: null },
    { id: 19, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "B", row: "8", seat: "4", ownershipStatus: "Principal archive", displayHistory: null, publicOwnerLabel: "Principal archive", evidenceStatus: "Documented", saleHistory: [{ date: "2025-10-16", venue: "eBay", price: 4050, premiumIncluded: null }], notes: "Light stains in top left corner" },
    { id: 20, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "B", row: "9", seat: "1", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: null, venue: "Heritage", price: null, premiumIncluded: null }], notes: "Framed with Elvis scarf and photographs" },
    { id: 21, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "B", row: "9", seat: "2", ownershipStatus: "Principal archive", displayHistory: null, publicOwnerLabel: "Principal archive", evidenceStatus: "Documented", saleHistory: [{ date: "2017-05-20", venue: "Julien's", price: 1600, premiumIncluded: null }, { date: "2023-11-08", venue: "RR Auction", price: 2500, premiumIncluded: null }], notes: "Multiple documented appearances" },
    { id: 22, physicalForm: "Handwritten VIP ticket stub", portionMissing: "Right side", graded: "Jack Lord provenance", section: "B", row: "9", seat: "15", ownershipStatus: "Institutional collection", displayHistory: "Displayed at Graceland", publicOwnerLabel: "Institutional collection", evidenceStatus: "Institutional display", saleHistory: [], notes: "Original VIP ticket with handwritten seat. Donated by Jack Lord (Hawaii Five-O). Many replicas exist on secondary markets." },
    { id: 23, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "BB", row: "8", seat: "1", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 24, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "CC", row: "6", seat: "5", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2014-08-21", venue: "eBay", price: 100, premiumIncluded: null }, { date: "2015-08-13", venue: "eBay", price: 2600, premiumIncluded: null }], notes: "Significant price appreciation between sales" },
    { id: 25, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "E", row: "2", seat: "12", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2011-05-03", venue: "eBay", price: 600, premiumIncluded: null }], notes: "Original holder attended concert" },
    { id: 26, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "E", row: "4", seat: "5", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "No documented sales" },
    { id: 27, physicalForm: "Used stub", portionMissing: "Right side", graded: "Graceland Authenticated", section: "F", row: "12", seat: "14", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2016-08-13", venue: "eBay", price: 1400, premiumIncluded: null }, { date: "2017-08-12", venue: "eBay", price: 1200, premiumIncluded: null }], notes: "Accompanied by Graceland authentication letter. Minor edge wear and small staining area on reverse." },
    { id: 28, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "M", row: "4", seat: "11", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2021-12-31", venue: "eBay", price: 2550, premiumIncluded: null }], notes: null },
    { id: 29, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "P", row: "8", seat: "3", ownershipStatus: "Institutional collection", displayHistory: "Displayed at Graceland", publicOwnerLabel: "Institutional collection", evidenceStatus: "Institutional display", saleHistory: [], notes: "Currently displayed at Graceland" },
    { id: 30, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "P", row: "8", seat: "4", ownershipStatus: "Institutional collection", displayHistory: "Displayed at Graceland", publicOwnerLabel: "Institutional collection", evidenceStatus: "Institutional display", saleHistory: [], notes: "Currently displayed at Graceland" },
    { id: 31, physicalForm: "Used stub", portionMissing: "Right side", graded: null, section: "P", row: "19", seat: "2", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    // FULL TICKETS - Seat-numbered
    { id: 32, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "A", row: "1", seat: "1", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Confirmed", saleHistory: [{ date: "2020-01-29", venue: "eBay", price: 1200, premiumIncluded: null }], notes: "Premium front-row seating" },
    { id: 33, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "AA", row: "19", seat: "1", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "No documented sales" },
    { id: 34, physicalForm: "Seat-numbered full ticket", portionMissing: "Taped right side", graded: null, section: "BB", row: "14", seat: "8", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2012-02-16", venue: "eBay", price: 1291, premiumIncluded: null }, { date: "2012-08-15", venue: "Heritage", price: 1600, premiumIncluded: true }], notes: "Documented provenance. Center fold crease. Right stub portion taped back on. Time handwritten correction: 1:00 AM crossed out, 12:30 AM written." },
    { id: 35, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "BB", row: "8", seat: "2", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 36, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "BB", row: "22", seat: "7", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2018-08-13", venue: "Heritage", price: 1700, premiumIncluded: true }], notes: null },
    { id: 37, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "BB", row: "21", seat: "6", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2014-04-27", venue: "Heritage", price: 1625, premiumIncluded: true }, { date: "2023-04-10", venue: "Heritage", price: 2760.36, premiumIncluded: true }], notes: "Offer To Owner sale" },
    { id: 38, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "BB", row: "21", seat: "7", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2014-08-24", venue: "Heritage", price: 1400, premiumIncluded: true }], notes: null },
    { id: 39, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "CC", row: "13", seat: "12", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Historical record", saleHistory: [], notes: "No documented sales" },
    { id: 40, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "CC", row: "14", seat: "5", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "Owner reported", saleHistory: [], notes: "Time correction: 12:30 written, 1:00 crossed out" },
    { id: 41, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "CC", row: "14", seat: "6", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2011-12-05", venue: "eBay", price: 1875, premiumIncluded: null }], notes: null },
    { id: 42, physicalForm: "Seat-numbered full ticket", portionMissing: null, graded: null, section: "N", row: "3", seat: "8", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2012-07-29", venue: "eBay", price: 1136.11, premiumIncluded: null }, { date: "2015-05-27", venue: "eBay", price: 1250, premiumIncluded: null }], notes: null },
    // BLANK FULL TICKETS
    { id: 43, physicalForm: "Blank full ticket", portionMissing: null, graded: null, section: "Blank", row: "Blank", seat: "Blank", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "Auction record", saleHistory: [{ date: "2011-07-21", venue: "eBay", price: 1250, premiumIncluded: null }], notes: "Ungraded blank ticket" },
    { id: 44, physicalForm: "Blank full ticket", portionMissing: null, graded: "PSA 6 (#43995269)", section: "Blank", row: "Blank", seat: "Blank", ownershipStatus: "Private collection", displayHistory: null, publicOwnerLabel: "Private collection", evidenceStatus: "PSA certified", saleHistory: [{ date: "2019-12-10", venue: "eBay", price: 749, premiumIncluded: null }], notes: "PSA-graded blank ticket" },
    { id: 45, physicalForm: "Blank full ticket", portionMissing: null, graded: "PSA 7 (#43995271)", section: "Blank", row: "Blank", seat: "Blank", relationship: "Owner undisclosed", publicOwnerLabel: "Current owner not established", evidenceStatus: "PSA certified", saleHistory: [{ date: "2019-12-10", venue: "eBay", price: 510, premiumIncluded: null }, { date: "2020-08-16", venue: "eBay", price: 1718, premiumIncluded: null }], notes: "Highest graded PSA blank ticket (Grade 7)" }
  ]
};

/* ============================================================================
   DATA: COLLECTION ARTIFACTS
   ============================================================================ */
const ARTIFACTS = [
  {
    id: 'seat-numbered-archive',
    catalogRef: 'EAA-2023-001',
    title: 'The Seat-Numbered Ticket Archive',
    category: 'Seat-Numbered Event Tickets',
    categoryId: 'seatNumbered',
    date: 'January 1973',
    creator: 'Globe Tickets, Los Angeles',
    format: 'Original event tickets on card stock',
    dimensions: 'Research pending',
    psaCert: 'Multiple certifications',
    psaGrade: 'Grades 5 through 8',
    psaDesignation: 'PROOF — Not Valid for Admission',
    classification: 'Seat-numbered original event tickets',
    admissionStatus: 'PSA designation: Not valid for admission',
    condition: 'Professionally graded by PSA',
    provenance: 'Acquired through documented sources; photographed at Graceland September 14, 2022',
    significance: 'These 50 tickets bear printed section, row, and seat assignments—making them original event tickets, not blank production proofs. PSA catalogs them under "PROOF — Not Valid for Admission," which this archive discloses separately from the physical classification.',
    evidenceStatus: 'PSA certification verified; Graceland documentation established',
    relatedItems: ['Production layouts', 'Printing negatives', 'Fred Haines signature'],
    image: '/assets/collection/real-graceland-costume-tickets-2.jpg',
    description: 'The 50 seat-numbered original event tickets form the certified centerpiece of the archive. Each ticket contains printed section, row, and seat information. PSA catalogs these under the designation "PROOF — Not Valid for Admission."'
  },
  {
    id: 'blank-full-tickets',
    catalogRef: 'EAA-2023-001B',
    title: 'Blank Full Tickets',
    category: 'Blank Full Tickets',
    categoryId: 'blankFull',
    date: 'January 1973',
    creator: 'Globe Tickets, Los Angeles',
    format: 'Blank tickets without seat assignments',
    dimensions: 'Research pending',
    psaCert: 'Multiple certifications',
    psaGrade: 'Grades 1.5+ through 7',
    psaDesignation: 'FULL — Aloha From Hawaii/H.I.C. Arena',
    classification: 'Proof-style blank tickets',
    admissionStatus: 'Blank — no assigned seating',
    condition: 'Professionally graded by PSA',
    provenance: 'Acquired through documented sources',
    significance: 'Unlike the seat-numbered tickets, these blank full tickets do not contain section, row, or seat information. They represent true proof-style examples from the production run.',
    evidenceStatus: 'PSA certification verified',
    relatedItems: ['Seat-numbered archive', 'Production layouts'],
    image: '/assets/collection/real-proof-artwork-grid.jpg',
    description: 'Blank full tickets without assigned seat numbers. These represent the true proof-style category—complete tickets that were never assigned specific seating information.'
  },
  {
    id: 'fred-haines-layout',
    catalogRef: 'EAA-2023-002',
    title: 'Signed Production Layout',
    category: 'Production Materials',
    categoryId: 'layouts',
    date: 'November 21, 1972',
    creator: 'Fred Haines, Globe Tickets (Los Angeles office)',
    format: 'Production layout on paper',
    dimensions: 'Research pending',
    classification: 'Production document',
    admissionStatus: 'N/A — Production material',
    condition: 'Research pending',
    provenance: 'Globe Tickets production archive; Fred Haines identification confirmed by Bob Smith (Globe Ticket General Manager) via company newsletter documentation, June 2021',
    significance: 'The signature and date (November 21, 1972) establish the production approval timeline. This is primary evidence for the ticket creation process.',
    evidenceStatus: 'Signature visible on document; Fred Haines confirmed via Globe Ticket newsletter',
    relatedItems: ['Seating zone plans', 'Printing negatives', 'Seat-numbered archive'],
    image: '/assets/collection/real-proof-artwork-grid.jpg',
    description: 'The final layout plan signed by Fred Haines of Globe Tickets\' Los Angeles office, dated November 21, 1972. This document establishes the production approval and connects the planning phase to the finished tickets.'
  },
  {
    id: 'prototype-mockup',
    catalogRef: 'EAA-2023-003',
    title: 'Early Prototype Design',
    category: 'Mockups',
    categoryId: 'mockups',
    date: '1972',
    creator: 'Globe Tickets / Artie Samuel (HIC Box Office Manager)',
    format: 'Ticket mockup with handwritten annotations',
    dimensions: 'Research pending',
    classification: 'Design prototype',
    admissionStatus: 'N/A — Prototype',
    condition: 'Research pending',
    provenance: 'Globe Tickets production archive',
    significance: 'Shows the design evolution with handwritten notes indicating required changes. Documents the souvenir-quality ticket concept attributed to Artie Samuel.',
    evidenceStatus: 'Physical document; creator attribution based on historical accounts',
    relatedItems: ['Signed layout', 'Seating plans', 'Seat-numbered archive'],
    image: '/assets/collection/real-proof-globe-portrait-1.jpg',
    description: 'Initial ticket mockup showing the design development process with handwritten annotations indicating changes and refinements before final production approval.'
  },
  {
    id: 'seating-plans',
    catalogRef: 'EAA-2023-004',
    title: 'Seating Zone Planning Documents',
    category: 'Production Materials',
    categoryId: 'plans',
    date: '1972',
    creator: 'Globe Tickets, Los Angeles',
    format: 'Planning documents on paper',
    dimensions: 'Research pending',
    classification: 'Production planning',
    admissionStatus: 'N/A — Planning document',
    condition: 'Research pending',
    provenance: 'Globe Tickets production archive',
    significance: 'Original planning documents showing HIC Arena, Loge, and Upper Level zones with ticket quantities and pricing tiers ($10.00, $7.50, $5.00).',
    evidenceStatus: 'Physical documents from production archive',
    relatedItems: ['Signed layout', 'Prototype mockup', 'Seat-numbered archive'],
    image: '/assets/collection/real-graceland-documents-1.jpg',
    description: 'Production planning documents establishing the seating zones and ticket distribution for the Honolulu International Center Arena.'
  }
];

/* ============================================================================
   DATA: GALLERY IMAGES
   ============================================================================ */
const GALLERY_IMAGES = [
  { src: '/assets/collection/real-graceland-costume-tickets-2.jpg', title: 'Complete Ticket Archive', category: 'PSA-Certified Proofs', caption: 'PSA-certified proof tickets documented at Graceland' },
  { src: '/assets/collection/real-proof-globe-portrait-1.jpg', title: 'Globe Portrait Design', category: 'Production Materials', caption: 'Original Globe Tickets production proof with portrait design' },
  { src: '/assets/collection/real-proof-artwork-grid.jpg', title: 'Production Artwork', category: 'Production Materials', caption: 'Color proofs and printing tests from Globe Tickets' },
  { src: '/assets/collection/real-graceland-documents-1.jpg', title: 'Production Documents', category: 'Documents', caption: 'Original correspondence and planning materials from 1972-1973' },
  { src: '/assets/collection/real-graceland-costume-tickets-1.jpg', title: 'Ticket Detail Study', category: 'Documentation', caption: 'Close examination of printing characteristics and design variations' },
  { src: '/assets/collection/real-graceland-framed-tickets-1.jpg', title: 'Framed Display', category: 'Graceland', caption: 'Collection materials in display configuration' },
  { src: '/assets/archive/graceland_exhibition_clean.png', title: 'Graceland Exhibition', category: 'Graceland', caption: 'The exhibition space featuring the Aloha jumpsuit display, September 14, 2022' },
  { src: '/assets/archive/collection_display_1.png', title: 'Collection Display', category: 'Graceland', caption: 'Archive materials documented at Graceland' }
];

const GALLERY_CATEGORIES = [
  'All',
  'PSA-Certified Proofs',
  'Production Materials',
  'Documents',
  'Graceland',
  'Documentation'
];

/* ============================================================================
   DATA: PRODUCTION LINEAGE
   ============================================================================ */
const PRODUCTION_STAGES = [
  {
    stage: 1,
    title: 'Concept Design',
    date: '1972',
    description: 'Artie Samuel, HIC Box Office Manager, conceptualized the souvenir-quality ticket design featuring Elvis\'s portrait.',
    evidence: 'Attribution based on historical accounts',
    image: '/assets/collection/real-proof-globe-portrait-1.jpg'
  },
  {
    stage: 2,
    title: 'Prototype Development',
    date: '1972',
    description: 'Globe Tickets created initial mockups with handwritten annotations showing required design changes.',
    evidence: 'Physical mockup documents in archive',
    image: '/assets/collection/real-graceland-documents-1.jpg'
  },
  {
    stage: 3,
    title: 'Layout Planning',
    date: '1972',
    description: 'Seating zone plans established for HIC Arena, Loge, and Upper Level with pricing tiers ($10, $7.50, $5).',
    evidence: 'Production planning documents',
    image: '/assets/collection/real-graceland-documents-1.jpg'
  },
  {
    stage: 4,
    title: 'Production Approval',
    date: 'November 21, 1972',
    description: 'Fred Haines of Globe Tickets\' Los Angeles office signed the final layout plan, establishing the production timeline.',
    evidence: 'Signature visible on document; identity confirmed via Globe Ticket newsletter',
    image: '/assets/collection/real-proof-artwork-grid.jpg'
  },
  {
    stage: 5,
    title: 'Printing Production',
    date: '1972-1973',
    description: 'Globe Tickets produced the tickets using original printing negatives, creating both production proofs and admission tickets.',
    evidence: 'Original negatives in archive',
    image: '/assets/collection/real-proof-artwork-grid.jpg'
  },
  {
    stage: 6,
    title: 'Event Use',
    date: 'January 14, 1973',
    description: 'Tickets used for admission to the benefit concert at Honolulu International Center Arena. Stubs detached at entry.',
    evidence: 'Used stubs with tear lines in collection',
    image: '/assets/collection/real-graceland-costume-tickets-2.jpg'
  }
];

/* ============================================================================
   IMAGES CONFIG
   ============================================================================ */
const IMAGES = {
  heroCollection: "/assets/archive/aloha_jumpsuit_display.jpg",
  globePortrait: "/assets/collection/real-proof-globe-portrait-1.jpg",
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  gracelandFramed: "/assets/collection/real-graceland-framed-tickets-1.jpg",
  gracelandWide: "/assets/archive/graceland_exhibition_clean.png"
};

/* ============================================================================
   OPTIMIZED IMAGE HELPERS
   Returns srcset for responsive loading with WebP support
   ============================================================================ */
const getOptimizedSrcSet = (basePath) => {
  const pathParts = basePath.split('/');
  const filename = pathParts[pathParts.length - 1];
  const nameWithoutExt = filename.replace(/\.(jpg|jpeg|png|webp)$/i, '');
  const folder = pathParts[pathParts.length - 2];
  const base = `/assets/optimized/${folder}`;
  
  return {
    webpSrcSet: `${base}/${nameWithoutExt}-thumb.webp 480w, ${base}/${nameWithoutExt}-std.webp 1200w`,
    jpgSrcSet: `${base}/${nameWithoutExt}-thumb.jpg 480w, ${base}/${nameWithoutExt}-std.jpg 1200w`,
    thumbWebp: `${base}/${nameWithoutExt}-thumb.webp`,
    stdWebp: `${base}/${nameWithoutExt}-std.webp`,
    fallback: basePath
  };
};

/* ============================================================================
   UTILITY: SMOOTH SCROLL
   ============================================================================ */
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

/* ============================================================================
   COMPONENT: NAVIGATION
   ============================================================================ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navItems = [
    { id: 'collection', label: 'Collection', href: '/collection' },
    { id: 'arena', label: 'Arena', href: '/arena' },
    { id: 'tickets', label: 'Tickets', href: '/tickets' },
    { id: 'research', label: 'Research', href: '/research' },
    { id: 'about', label: 'About', scroll: 'about' }
  ];

  return (
    <>
      <style>{`
        /* Skip to content link - WCAG 2.4.1 */
        .skip-link {
          position: absolute;
          top: -100px;
          left: 50%;
          transform: translateX(-50%);
          background: #1a1a1a;
          color: white;
          padding: 12px 24px;
          text-decoration: none;
          font-family: 'Source Sans 3', sans-serif;
          font-weight: 600;
          z-index: 1000;
          border-radius: 0 0 4px 4px;
          transition: top 0.2s;
        }
        .skip-link:focus {
          top: 0;
          outline: 3px solid #C9A962;
          outline-offset: 2px;
        }
        
        /* Reduced motion support - WCAG 2.3.3 */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
        
        /* Focus visible styles - WCAG 2.4.7 */
        :focus-visible {
          outline: 3px solid #C9A962;
          outline-offset: 2px;
        }
        
        button:focus-visible, a:focus-visible {
          outline: 3px solid #C9A962;
          outline-offset: 2px;
        }
        
        .nav-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: all 0.3s ease;
          background: ${scrolled ? 'rgba(255,255,255,0.98)' : 'transparent'};
          box-shadow: ${scrolled ? '0 1px 0 rgba(0,0,0,0.08)' : 'none'};
        }
        .nav-link {
          padding: 10px 16px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
          color: ${scrolled ? '#333' : 'rgba(255,255,255,0.9)'};
          min-height: 44px;
          min-width: 44px;
          display: inline-flex;
          align-items: center;
        }
        .nav-link:hover { color: ${scrolled ? '#8B7355' : '#C9A962'}; }
        .nav-link:focus-visible { 
          outline: 3px solid ${scrolled ? '#8B7355' : '#C9A962'};
          outline-offset: 2px;
        }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
        @media (min-width: 901px) {
          .desktop-nav { display: flex !important; }
          .mobile-toggle { display: none !important; }
        }
      `}</style>

      {/* Skip to main content link - WCAG 2.4.1 */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <nav className="nav-container" data-testid="nav" role="navigation" aria-label="Main navigation">
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <img src="/assets/branding/elvis-logo-illustration.png" alt="Elvis Aloha Collection" style={{ height: '48px', width: 'auto', filter: scrolled ? 'none' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, color: scrolled ? '#1a1a1a' : 'white', letterSpacing: '0.06em' }}>
              ELVIS ALOHA COLLECTION
            </span>
          </a>

          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {navItems.map(item => (
              item.href ? (
                <Link key={item.id} className="nav-link" to={item.href} style={{ textDecoration: 'none' }}>
                  {item.label}
                </Link>
              ) : (
                <button key={item.id} className="nav-link" onClick={() => scrollToSection(item.scroll || item.id)}>
                  {item.label}
                </button>
              )
            ))}
          </div>

          <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={scrolled ? '#1a1a1a' : 'white'} strokeWidth="1.5" aria-hidden="true">
              {mobileOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src="/assets/branding/elvis-logo-illustration.png" alt="Elvis Aloha Collection" style={{ height: '44px', width: 'auto' }} />
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, color: '#1a1a1a' }}>ELVIS ALOHA COLLECTION</span>
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Close navigation menu" style={{ background: 'none', border: 'none', padding: '12px', cursor: 'pointer', minWidth: '44px', minHeight: '44px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding: '24px 32px', flex: 1 }}>
              {navItems.map(item => (
                item.href ? (
                  <Link key={item.id} to={item.href} onClick={() => setMobileOpen(false)}
                    style={{ display: 'block', width: '100%', padding: '18px 0', textAlign: 'left', background: 'none', border: 'none', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1a1a1a', textDecoration: 'none', borderBottom: '1px solid #f0f0f0' }}>
                    {item.label}
                  </Link>
                ) : (
                  <button key={item.id} onClick={() => { scrollToSection(item.scroll || item.id); setMobileOpen(false); }}
                    style={{ display: 'block', width: '100%', padding: '18px 0', textAlign: 'left', background: 'none', border: 'none', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1a1a1a', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                    {item.label}
                  </button>
                )
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

/* ============================================================================
   COMPONENT: HERO
   ============================================================================ */
const HeroSection = () => (
  <section id="top" role="banner" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }} data-testid="hero">
    <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${IMAGES.heroCollection})`, backgroundSize: 'cover', backgroundPosition: 'center' }} aria-hidden="true" />
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.58), rgba(0,0,0,0.68), rgba(0,0,0,0.62))', pointerEvents: 'none' }} aria-hidden="true" />

    <div style={{ position: 'relative', width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '140px 32px 100px', textAlign: 'center' }}>
      <p style={{ marginBottom: '20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#C9A962', textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)' }}>
        A Documented Historical Collection
      </p>

      <h1 style={{ margin: '0 0 28px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px, 6vw, 58px)', fontWeight: 400, lineHeight: 1.15, color: 'white', letterSpacing: '-0.01em', textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)' }}>
        The Elvis Presley<br /><em>Aloha from Hawaii</em><br />Ticket &amp; Production Collection
      </h1>

      <p style={{ maxWidth: '720px', margin: '0 auto 40px', fontFamily: "'Source Serif 4', serif", fontSize: 'clamp(16px, 2.2vw, 19px)', lineHeight: 1.75, color: 'rgba(255,255,255,0.95)', textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)' }}>
        Original design mockups, PSA-certified seat-numbered tickets, admission artifacts, production materials, 
        and documentation connected to Elvis Presley's January 1973 benefit concert at the 
        Honolulu International Center Arena.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '48px' }} role="list" aria-label="Collection statistics">
        {[
          { value: '50', label: 'PSA-Certified Tickets' },
          { value: 'Production', label: 'Mockups & Layouts' },
          { value: 'Sept 14, 2022', label: 'Graceland Documented' }
        ].map((stat, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: '#C9A962', textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)' }}>{stat.value}</p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)' }}>{stat.label}</p>
          </div>
        ))}
      </div>
    </div>

    <button onClick={() => scrollToSection('inventory')} style={{ position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '12px', minWidth: '44px', minHeight: '44px' }} aria-label="Scroll to content">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
    </button>
  </section>
);

/* ============================================================================
   COMPONENT: INVENTORY SECTION
   ============================================================================ */
const InventorySection = () => {
  const statusColors = {
    'Verified': { bg: '#e8f5e9', color: '#2e7d32', border: '#c8e6c9' },
    'Provisional': { bg: '#fff3e0', color: '#e65100', border: '#ffe0b2' },
    'Under reconciliation': { bg: '#e3f2fd', color: '#1565c0', border: '#bbdefb' },
    'Not yet inventoried': { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0' }
  };
  
  return (
    <section id="inventory" style={{ padding: '100px 32px', background: 'white' }} data-testid="inventory">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Collection Inventory</p>
          <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>What the Archive Contains</h2>
          <p style={{ maxWidth: '750px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: '#555' }}>
            A substantial surviving ticket and production archive. Understanding these categories is essential—<strong>not every item is an "original ticket."</strong>
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '40px' }}>
          {COLLECTION_INVENTORY.categories.map((cat, idx) => {
            const statusStyle = statusColors[cat.status] || statusColors['Provisional'];
            const isTicket = ['seatNumbered', 'blankFull', 'stubs'].includes(cat.id);
            return (
              <div key={idx} style={{ 
                padding: '20px 24px', 
                background: isTicket ? '#f8f6f3' : '#fafafa', 
                border: `1px solid ${isTicket ? '#d4c9b8' : '#e8e8e8'}`, 
                borderLeft: isTicket ? '4px solid #8B7355' : '1px solid #e8e8e8' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>{cat.label}</h3>
                  <span style={{ 
                    padding: '2px 8px', 
                    fontSize: '10px', 
                    fontWeight: 600, 
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    background: statusStyle.bg, 
                    color: statusStyle.color,
                    border: `1px solid ${statusStyle.border}`,
                    borderRadius: '2px',
                    fontFamily: "'Source Sans 3', sans-serif"
                  }}>
                    {cat.status}
                  </span>
                </div>
                <p style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B7355' }}>{cat.count}</p>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.55, color: '#666' }}>{cat.note}</p>
              </div>
            );
          })}
        </div>

        {/* Classification distinction box */}
        <div style={{ padding: '24px 28px', background: '#1a1a1a', color: 'white' }}>
          <h4 style={{ margin: '0 0 10px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>Important: Physical Form vs PSA Designation</h4>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.7, color: 'rgba(255,255,255,0.85)' }}>
            The 50 seat-numbered tickets contain printed section, row, and seat assignments—they are not blank proofs. However, PSA catalogs them under <strong>"PROOF — Not Valid for Admission."</strong> This archive presents both the physical classification and the PSA designation separately.
          </p>
        </div>
        
        {/* Status legend */}
        <div style={{ marginTop: '24px', padding: '16px 20px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
          <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#666' }}>Verification Status Legend</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {Object.entries(statusColors).map(([status, style]) => (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ display: 'inline-block', width: '12px', height: '12px', background: style.bg, border: `1px solid ${style.border}`, borderRadius: '2px' }}></span>
                <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#555' }}>{status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   COMPONENT: CATALOG SECTION
   ============================================================================ */
const CatalogSection = ({ onArtifactSelect, onImageClick }) => (
  <section id="catalog" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="catalog">
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Principal Artifacts</p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>Collection Catalog</h2>
        <p style={{ maxWidth: '650px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: '#555' }}>
          Select an artifact to view complete documentation including provenance, certification, and historical significance.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))', gap: '24px' }}>
        {ARTIFACTS.map((artifact) => (
          <article key={artifact.id} style={{ background: 'white', border: '1px solid #e0ddd8', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
            onClick={() => onArtifactSelect(artifact)}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>
            <div style={{ position: 'relative', paddingTop: '60%', overflow: 'hidden', background: '#e8e6e2' }}
              onClick={(e) => { e.stopPropagation(); onImageClick(artifact.image, artifact.title); }}>
              <img 
                src={artifact.image} 
                alt={artifact.title} 
                loading="lazy"
                decoding="async"
                width="400"
                height="240"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
              />
              <div style={{ position: 'absolute', top: '12px', left: '12px', padding: '4px 10px', background: 'rgba(0,0,0,0.7)', borderRadius: '2px' }}>
                <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'white' }}>{artifact.catalogRef}</span>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8B7355' }}>{artifact.category}</p>
              <h3 style={{ margin: '0 0 8px', fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 500, color: '#1a1a1a' }}>{artifact.title}</h3>
              <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.55, color: '#666' }}>{artifact.description.substring(0, 120)}...</p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: '#888' }}><strong>Date:</strong> {artifact.date}</span>
                {artifact.psaGrade && <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: '#8B7355', fontWeight: 600 }}>PSA Certified</span>}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: INTERACTIVE SEATING CHART SECTION
   Explore Known Tickets by Seat
   ============================================================================ */
const SeatingChartSection = () => (
  <section 
    id="seat-map"
    style={{
      padding: '100px 24px',
      background: '#fafafa'
    }}
    data-testid="seat-map"
  >
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Section Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <p style={{
          marginBottom: '12px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#C9A962'
        }}>
          Interactive Arena Explorer
        </p>
        <h2 style={{
          margin: '0 0 16px',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(28px, 4.5vw, 42px)',
          fontWeight: 400,
          color: '#1a1a1a'
        }}>
          Explore the Arena & Ticket Registry
        </h2>
        <p style={{
          margin: '0 auto 32px',
          maxWidth: '700px',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '17px',
          lineHeight: 1.75,
          color: '#555'
        }}>
          Navigate the complete Neal S. Blaisdell Arena seating chart to discover documented tickets 
          from the Aloha from Hawaii concert. Explore venue layouts, seating sections, the Golden Circle, 
          production areas, and surviving ticket locations.
        </p>
      </div>
      
      {/* Preview Card with Arena Image */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 30px rgba(0,0,0,0.08)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Arena Preview Image - Centered and Clickable */}
        <a 
          href="/ticket-registry"
          style={{
            display: 'block',
            padding: '40px',
            background: 'linear-gradient(180deg, #f8f8f8 0%, #ffffff 100%)',
            cursor: 'pointer',
            textAlign: 'center'
          }}
        >
          <img 
            src="/assets/charts/arena-stage-layout-1.png"
            alt="Neal S. Blaisdell Arena - Concert Stage Layout"
            style={{
              maxWidth: '600px',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease'
            }}
          />
        </a>
        
        {/* Features List */}
        <div style={{
          padding: '24px 40px',
          display: 'flex',
          justifyContent: 'center',
          gap: '32px',
          flexWrap: 'wrap',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎪</div>
            <div style={{ fontSize: '13px', color: '#666' }}>3 Venue Layouts</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎫</div>
            <div style={{ fontSize: '13px', color: '#666' }}>Documented Tickets</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📍</div>
            <div style={{ fontSize: '13px', color: '#666' }}>Exact Seat Locations</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔍</div>
            <div style={{ fontSize: '13px', color: '#666' }}>Historical Research</div>
          </div>
        </div>
        
        {/* CTA Button */}
        <div style={{ padding: '0 40px 40px' }}>
          <a 
            href="/ticket-registry"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px 40px',
              background: '#C9A962',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '16px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(201, 169, 98, 0.3)'
            }}
            data-testid="explore-registry-btn"
          >
            Explore the Arena & Ticket Registry
            <span style={{ fontSize: '18px' }}>→</span>
          </a>
        </div>
      </div>
      
      {/* Attribution Note */}
      <div style={{
        marginTop: '32px',
        padding: '20px 24px',
        background: 'rgba(201, 169, 98, 0.08)',
        border: '1px solid rgba(201, 169, 98, 0.2)',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{
          margin: 0,
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '13px',
          color: '#8a7842',
          lineHeight: 1.7
        }}>
          <strong>Venue Reference Chart:</strong> The seating chart provides the underlying arena reference structure. 
          The Aloha Concert Reconstruction overlay is a developing research reconstruction based on surviving tickets, 
          photographs, production materials, and contemporary documentation.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: PSA POPULATION SECTION
   Mobile-responsive: Converts to cards below 640px viewport width
   ============================================================================ */
const PSASection = () => (
  <section style={{ padding: '100px 32px', background: 'white' }} data-testid="psa">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Certification Data</p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>PSA Population Report</h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: '#555' }}>
          The following tables show the total PSA population for Aloha from Hawaii tickets. <strong>This archive does not claim to own the entire PSA population.</strong>
        </p>
        <p style={{ maxWidth: '700px', margin: '16px auto 0', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#777', fontStyle: 'italic' }}>
          Note: PSA catalog labels do not always describe the physical nature of the tickets. This archive classifies tickets by their physical characteristics and separately discloses PSA's catalog designation.
        </p>
      </div>

      {Object.entries(PSA_POPULATION.categories).map(([key, cat]) => (
        <div key={key} style={{ marginBottom: '40px', padding: '24px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
          <h3 style={{ margin: '0 0 8px', fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 500, color: '#1a1a1a' }}>{cat.label}</h3>
          <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: '#666', lineHeight: 1.6 }}>
            {cat.physicalDescription}
          </p>
          <p style={{ margin: '0 0 20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', lineHeight: 1.5 }}>
            <strong>PSA Catalog Designation:</strong> {cat.psaCatalogDesignation}
          </p>
          
          {/* Desktop Table */}
          <div className="psa-table-desktop" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '400px', borderCollapse: 'collapse', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', background: 'white' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e0ddd8', background: '#f5f5f3' }}>Grade</th>
                  {Object.keys(cat.grades).map(grade => (
                    <th key={grade} style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e0ddd8', background: '#f5f5f3' }}>{grade}</th>
                  ))}
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, borderBottom: '2px solid #e0ddd8', background: '#f5f5f3', color: '#8B7355' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e8e6e2', fontWeight: 500 }}>Population</td>
                  {Object.values(cat.grades).map((count, idx) => (
                    <td key={idx} style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #e8e6e2' }}>{count}</td>
                  ))}
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #e8e6e2', color: '#8B7355' }}>{cat.psaTotal}</td>
                </tr>
                <tr style={{ background: '#fafaf8' }}>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e8e6e2' }}>Collection Owned</td>
                  <td colSpan={Object.keys(cat.grades).length + 1} style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #e8e6e2', fontStyle: 'italic', color: '#888' }}>
                    {cat.collectionOwned}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Mobile Cards (shown via CSS on small screens) */}
          <div className="psa-cards-mobile" style={{ display: 'none' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))', gap: '8px', marginBottom: '12px' }}>
              {Object.entries(cat.grades).map(([grade, count]) => (
                <div key={grade} style={{ padding: '12px 8px', background: 'white', border: '1px solid #e8e6e2', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase' }}>Grade {grade}</p>
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a1a1a' }}>{count}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'white', border: '2px solid #8B7355' }}>
              <span style={{ fontWeight: 600, color: '#666' }}>Total PSA Population</span>
              <span style={{ fontWeight: 700, fontSize: '18px', color: '#8B7355' }}>{cat.psaTotal}</span>
            </div>
            <div style={{ marginTop: '8px', padding: '10px 16px', background: '#f5f5f3', border: '1px solid #e8e6e2' }}>
              <span style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>Collection Owned: {cat.collectionOwned}</span>
            </div>
          </div>
        </div>
      ))}

      <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', lineHeight: 1.6, fontStyle: 'italic', marginTop: '24px' }}>
        Population data captured as of {PSA_POPULATION.captureDate}. {PSA_POPULATION.note}
      </p>
      
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 640px) {
          .psa-table-desktop { display: none !important; }
          .psa-cards-mobile { display: block !important; }
        }
      `}</style>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: PRODUCTION LINEAGE
   ============================================================================ */
const ProductionSection = ({ onImageClick }) => (
  <section id="production" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="production">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Production Lineage</p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>From Concept to Event</h2>
        <p style={{ maxWidth: '650px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: '#555' }}>
          The archive preserves materials showing how these historic tickets were created—each stage documented with surviving artifacts.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '24px' }}>
        {PRODUCTION_STAGES.map((stage) => (
          <div key={stage.stage} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '32px', padding: '28px', background: 'white', border: '1px solid #e0ddd8', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#8B7355', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700 }}>{stage.stage}</span>
                <span style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B7355' }}>{stage.date}</span>
              </div>
              <h3 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontWeight: 500, color: '#1a1a1a' }}>{stage.title}</h3>
              <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#555' }}>{stage.description}</p>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                <strong>Evidence:</strong> {stage.evidence}
              </p>
            </div>
            <div style={{ cursor: 'pointer' }} onClick={() => onImageClick(stage.image, stage.title)}>
              <img 
                src={stage.image} 
                alt={stage.title} 
                loading="lazy"
                decoding="async"
                width="400"
                height="300"
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }} 
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: GRACELAND SECTION
   ============================================================================ */
const GracelandSection = ({ onImageClick }) => (
  <section id="graceland" style={{ padding: '100px 32px', background: '#0a0a0a', color: 'white' }} data-testid="graceland">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#C9A962' }}>Provenance Documentation</p>
        <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, lineHeight: 1.2 }}>Documented at Graceland</h2>
        <p style={{ maxWidth: '750px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: 'rgba(255,255,255,0.8)' }}>
          A substantial group of original Aloha tickets and production materials from the archive was documented in connection with the Graceland Aloha display on <strong>September 14, 2022</strong>.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '24px', marginBottom: '40px', alignItems: 'start' }}>
        <div style={{ cursor: 'pointer' }} onClick={() => onImageClick(IMAGES.gracelandWide, 'Graceland Exhibition Space')}>
          <div style={{ position: 'relative', height: '280px', overflow: 'hidden', background: '#1a1a1a' }}>
            <img 
              src={IMAGES.gracelandWide} 
              alt="Graceland exhibition space featuring the Aloha from Hawaii display" 
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
          </div>
          <p style={{ marginTop: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
            The exhibition space featuring the Aloha jumpsuit display where collection materials were documented.
          </p>
        </div>
        <div style={{ cursor: 'pointer' }} onClick={() => onImageClick(IMAGES.heroCollection, 'Ticket Archive Display')}>
          <div style={{ position: 'relative', height: '280px', overflow: 'hidden', background: '#1a1a1a' }}>
            <img 
              src={IMAGES.heroCollection} 
              alt="Original Aloha from Hawaii tickets documented at Graceland" 
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
          </div>
          <p style={{ marginTop: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
            Archive materials documented at Graceland, showing the scope of preserved artifacts.
          </p>
        </div>
      </div>

      <div style={{ padding: '28px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>Documentation Statement</h3>
        <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: 'rgba(255,255,255,0.85)' }}>
          A substantial group of original Aloha tickets and production materials from the archive was documented in connection with the Graceland Aloha display on <strong>September 14, 2022</strong>.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
          Note: This documentation does not constitute formal authentication by Graceland or Elvis Presley Enterprises. The photographs establish the collection's presence at Graceland on the documented date.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: GALLERY SECTION
   ============================================================================ */
const GallerySection = ({ onImageClick }) => {
  const [filter, setFilter] = useState('All');
  const filteredImages = filter === 'All' ? GALLERY_IMAGES : GALLERY_IMAGES.filter(img => img.category === filter);

  return (
    <section id="gallery" style={{ padding: '100px 32px', background: 'white' }} data-testid="gallery">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Visual Archive</p>
          <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>Collection Gallery</h2>
        </div>

        {/* Filter buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '40px' }} role="group" aria-label="Filter gallery by category">
          {GALLERY_CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              aria-pressed={filter === cat}
              style={{ padding: '12px 20px', minHeight: '44px', background: filter === cat ? '#1a1a1a' : '#f5f5f5', color: filter === cat ? 'white' : '#555', border: 'none', borderRadius: '4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery grid - Optimized images with lazy loading and srcset */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {filteredImages.map((img, idx) => {
            const srcSet = getOptimizedSrcSet(img.src);
            return (
              <figure key={idx} style={{ margin: 0, cursor: 'pointer' }} onClick={() => onImageClick(img.src, img.title, img.caption, img.category)}>
                <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden', background: '#f0f0f0', borderRadius: '4px' }}>
                  <picture>
                    <source type="image/webp" srcSet={srcSet.webpSrcSet} sizes="(max-width: 600px) 100vw, 400px" />
                    <source type="image/jpeg" srcSet={srcSet.jpgSrcSet} sizes="(max-width: 600px) 100vw, 400px" />
                    <img 
                      src={srcSet.thumbWebp || img.src}
                      alt={img.title} 
                      loading="lazy"
                      decoding="async"
                      width="400"
                      height="300"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.03)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'} 
                    />
                  </picture>
                </div>
                <figcaption style={{ padding: '12px 0' }}>
                  <p style={{ margin: '0 0 2px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8B7355' }}>{img.category}</p>
                  <h4 style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>{img.title}</h4>
                  <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888', lineHeight: 1.4 }}>{img.caption}</p>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   COMPONENT: ABOUT SECTION
   ============================================================================ */
const AboutSection = () => (
  <section id="about" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="about">
    <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
      <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Purpose</p>
      <h2 style={{ margin: '0 0 32px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a' }}>About the Archive</h2>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '17px', lineHeight: 1.85, color: '#444', textAlign: 'left' }}>
        <p style={{ marginBottom: '20px' }}>
          The Elvis Aloha Collection exists to preserve and document materials from the January 1973 Aloha from Hawaii benefit concert. The collection maintains these materials together, preserving the relationships between production documents, proofs, and finished tickets.
        </p>
        <p style={{ marginBottom: '20px' }}>
          The archive is committed to accurate historical presentation. Claims are supported by evidence, uncertain findings are appropriately qualified, and open questions are acknowledged rather than filled with speculation.
        </p>
        <p>
          The collection distinguishes between different categories—PSA-certified proofs, admission tickets, used stubs, production mockups, and supporting documentation—rather than combining them under misleading generalizations.
        </p>
      </div>

      <div style={{ marginTop: '48px', padding: '28px', background: 'white', border: '1px solid #e0ddd8' }}>
        <h3 style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>Inquiries</h3>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#666' }}>
          Researchers, institutions, and serious inquiries may contact the archive for additional information, access to documentation, or scholarly consultation.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: KNOWN TICKETS REGISTRY SECTION
   
   Research registry of other known Aloha tickets documented through
   private collections, institutional displays, auction records, dealer 
   listings, certification records, and historical photographs.
   
   This section is SEPARATE from PSA population and collection inventory.
   ============================================================================ */
const KnownTicketsRegistrySection = () => {
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [filter, setFilter] = useState('all');
  
  const summary = KNOWN_TICKETS_REGISTRY.summary;
  const tickets = KNOWN_TICKETS_REGISTRY.tickets;
  
  // Filter tickets based on selection
  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'stubs') return t.physicalForm.toLowerCase().includes('stub');
    if (filter === 'full') return t.physicalForm.toLowerCase().includes('full ticket');
    if (filter === 'blank') return t.physicalForm.toLowerCase().includes('blank');
    if (filter === 'graceland') return t.displayHistory && t.displayHistory.includes('Graceland');
    if (filter === 'archive') return t.ownershipStatus === 'Principal archive';
    if (filter === 'private') return t.ownershipStatus === 'Private collection';
    if (filter === 'unknown') return t.ownershipStatus === 'Current owner not established';
    return true;
  });
  
  const formatPrice = (price) => {
    if (!price) return '—';
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };
  
  return (
    <section id="registry" style={{ padding: '100px 32px', background: '#fafaf8' }} data-testid="known-tickets">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Research Documentation</p>
          <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>The Broader Known Ticket Record</h2>
          <p style={{ maxWidth: '750px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '17px', lineHeight: 1.75, color: '#555' }}>
            In addition to the principal archive, ongoing research has documented other surviving Aloha tickets through private collections, institutional displays, auction records, dealer listings, certification records, and historical photographs. This registry records known examples by ticket form, seating information, condition, ownership history, and source documentation.
          </p>
        </div>
        
        {/* Summary Statistics - Separated Ownership and Display */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#666' }}>By Physical Form</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Total Records', value: summary.totalRecords },
              { label: 'Full Tickets', value: summary.fullTicketRecords },
              { label: 'Stubs', value: summary.stubRecords },
              { label: 'Blank Tickets', value: summary.blankTicketRecords },
            ].map(stat => (
              <div key={stat.label} style={{ padding: '16px', background: 'white', border: '1px solid #e0ddd8', textAlign: 'center' }}>
                <p style={{ margin: '0 0 4px', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: '#8B7355' }}>{stat.value}</p>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>{stat.label}</p>
              </div>
            ))}
          </div>
          
          <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#666' }}>By Ownership Status</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Principal Archive', value: tickets.filter(t => t.ownershipStatus === 'Principal archive').length, color: '#2e7d32' },
              { label: 'Private Collection', value: tickets.filter(t => t.ownershipStatus === 'Private collection').length, color: '#e65100' },
              { label: 'Institutional', value: tickets.filter(t => t.ownershipStatus === 'Institutional collection').length, color: '#1565c0' },
              { label: 'Owner Unknown', value: tickets.filter(t => t.ownershipStatus === 'Current owner not established').length, color: '#666' },
            ].map(stat => (
              <div key={stat.label} style={{ padding: '16px', background: 'white', border: '1px solid #e0ddd8', textAlign: 'center' }}>
                <p style={{ margin: '0 0 4px', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: stat.color }}>{stat.value}</p>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>{stat.label}</p>
              </div>
            ))}
          </div>
          
          <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#666' }}>By Display History</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            {[
              { label: 'Displayed at Graceland', value: tickets.filter(t => t.displayHistory && t.displayHistory.includes('Graceland')).length, color: '#1565c0' },
              { label: 'With Sale History', value: summary.recordsWithSaleHistory, color: '#8B7355' },
            ].map(stat => (
              <div key={stat.label} style={{ padding: '16px', background: 'white', border: '1px solid #e0ddd8', textAlign: 'center' }}>
                <p style={{ margin: '0 0 4px', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 500, color: stat.color }}>{stat.value}</p>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Important Disclaimer - Updated with deduplication analysis */}
        <div style={{ padding: '20px 24px', background: '#fff8e6', border: '1px solid #e6d9a8', marginBottom: '16px' }}>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#7a6832' }}>
            <strong>Research Note:</strong> The research registry currently contains <strong>41 confirmed distinct ticket examples</strong> and four records that remain under review. <strong>No confirmed duplicate records have been identified</strong>. Multiple listings or sales of the same physical ticket have been consolidated under single ticket identities. This count does not represent the complete surviving population. Final counts are subject to ongoing research.
          </p>
        </div>
        
        {/* Privacy Notice */}
        <div style={{ padding: '16px 24px', background: '#f5f5f5', border: '1px solid #e0e0e0', marginBottom: '32px' }}>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.6, color: '#666' }}>
            <strong>Privacy:</strong> Current owner identities are not disclosed on this public registry. Private collectors are identified only as "Private collection" or "Owner undisclosed."
          </p>
        </div>
        
        {/* Filter Tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }} role="group" aria-label="Filter registry records">
          {[
            { id: 'all', label: 'All Records' },
            { id: 'full', label: 'Full Tickets' },
            { id: 'stubs', label: 'Ticket Stubs' },
            { id: 'blank', label: 'Blank Tickets' },
            { id: 'archive', label: 'Principal Archive' },
            { id: 'private', label: 'Private Collection' },
            { id: 'unknown', label: 'Owner Unknown' },
            { id: 'graceland', label: 'Graceland Display' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              aria-pressed={filter === tab.id}
              style={{
                padding: '10px 16px',
                minHeight: '44px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '13px',
                fontWeight: filter === tab.id ? 700 : 500,
                border: '1px solid #e0ddd8',
                borderRadius: '4px',
                background: filter === tab.id ? '#8B7355' : 'white',
                color: filter === tab.id ? 'white' : '#666',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Registry Table */}
        <div style={{ background: 'white', border: '1px solid #e0ddd8', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f5f5f3' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Physical Form</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Sec/Row/Seat</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Status</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Grading</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Last Sale</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e0ddd8' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <tr style={{ background: expandedTicket === ticket.id ? '#f9f9f7' : 'white', borderBottom: expandedTicket === ticket.id ? 'none' : '1px solid #e8e6e2' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 500 }}>{ticket.physicalForm}</span>
                        {ticket.portionMissing && (
                          <span style={{ display: 'block', fontSize: '12px', color: '#888' }}>{ticket.portionMissing}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {ticket.section === 'Blank' ? (
                          <span style={{ color: '#888', fontStyle: 'italic' }}>Blank</span>
                        ) : ticket.section ? (
                          <span>{ticket.section}/{ticket.row}/{ticket.seat}</span>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          fontSize: '11px',
                          fontWeight: 600,
                          borderRadius: '3px',
                          background: 
                            ticket.ownershipStatus === 'Principal archive' ? '#e8f5e9' :
                            ticket.ownershipStatus === 'Institutional collection' ? '#e3f2fd' :
                            ticket.ownershipStatus === 'Private collection' ? '#fff3e0' : '#f5f5f5',
                          color:
                            ticket.ownershipStatus === 'Principal archive' ? '#2e7d32' :
                            ticket.ownershipStatus === 'Institutional collection' ? '#1565c0' :
                            ticket.ownershipStatus === 'Private collection' ? '#e65100' : '#666'
                        }}>
                          {ticket.ownershipStatus || ticket.publicOwnerLabel}
                        </span>
                        {ticket.displayHistory && (
                          <span style={{ display: 'block', fontSize: '10px', color: '#1565c0', marginTop: '2px' }}>
                            {ticket.displayHistory}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: ticket.graded ? '#333' : '#888' }}>
                        {ticket.graded || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {ticket.saleHistory && ticket.saleHistory.length > 0 && ticket.saleHistory[ticket.saleHistory.length - 1].price ? (
                          <>
                            <span style={{ fontWeight: 600, color: '#8B7355' }}>{formatPrice(ticket.saleHistory[ticket.saleHistory.length - 1].price)}</span>
                            <span style={{ display: 'block', fontSize: '11px', color: '#888' }}>{ticket.saleHistory[ticket.saleHistory.length - 1].date}</span>
                          </>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            border: '1px solid #e0ddd8',
                            borderRadius: '3px',
                            background: 'white',
                            color: '#666',
                            cursor: 'pointer'
                          }}
                        >
                          {expandedTicket === ticket.id ? 'Close' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {expandedTicket === ticket.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 16px 16px', background: '#f9f9f7', borderBottom: '1px solid #e8e6e2' }}>
                          <div style={{ padding: '16px', background: 'white', border: '1px solid #e8e6e2', borderRadius: '4px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: ticket.saleHistory.length > 0 ? '16px' : 0 }}>
                              <div>
                                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Physical Classification</p>
                                <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{ticket.physicalForm}</p>
                              </div>
                              <div>
                                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Ownership Status</p>
                                <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{ticket.ownershipStatus || ticket.publicOwnerLabel}</p>
                              </div>
                              {ticket.displayHistory && (
                                <div>
                                  <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Display History</p>
                                  <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>{ticket.displayHistory}</p>
                                </div>
                              )}
                              <div>
                                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Evidence Status</p>
                                <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>{ticket.evidenceStatus}</p>
                              </div>
                              {ticket.notes && (
                                <div style={{ gridColumn: 'span 2' }}>
                                  <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Research Notes</p>
                                  <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: 1.6 }}>{ticket.notes}</p>
                                </div>
                              )}
                            </div>
                            {ticket.saleHistory && ticket.saleHistory.length > 0 && ticket.saleHistory.some(s => s.date || s.price) && (
                              <div>
                                <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>Documented Sale History</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {ticket.saleHistory.filter(s => s.date || s.price).map((sale, idx) => (
                                    <div key={idx} style={{ padding: '8px 12px', background: '#f5f5f3', borderRadius: '4px', fontSize: '13px' }}>
                                      {sale.date && <span style={{ color: '#666' }}>{sale.date}</span>}
                                      {sale.venue && <span style={{ color: '#888' }}> · {sale.venue}</span>}
                                      {sale.price && <span style={{ fontWeight: 600, color: '#8B7355' }}> · {formatPrice(sale.price)}</span>}
                                      {sale.premiumIncluded && <span style={{ fontSize: '11px', color: '#888' }}> (incl. premium)</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {ticket.sourceLink && (
                              <div style={{ marginTop: '12px' }}>
                                <a href={ticket.sourceLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#8B7355' }}>View source →</a>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Sale Price Disclaimer */}
        <div style={{ marginTop: '24px', padding: '20px 24px', background: 'white', border: '1px solid #e0ddd8' }}>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.7, color: '#666', fontStyle: 'italic' }}>
            <strong>Documented Historical Sale Results:</strong> Recorded sale results are included as part of each ticket's documented history. They reflect different ticket forms, conditions, provenance, certification status, sale venues, and market periods and are not current appraisals. Prices may or may not include buyer's premiums. This registry does not calculate average prices or combine unlike ticket types.
          </p>
        </div>
        
        {/* Source Attribution */}
        <p style={{ marginTop: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: '#888', lineHeight: 1.6 }}>
          Registry source: {KNOWN_TICKETS_REGISTRY.sourceFile} · Last updated: {KNOWN_TICKETS_REGISTRY.lastUpdated} · {KNOWN_TICKETS_REGISTRY.status}
        </p>
      </div>
    </section>
  );
};

/* ============================================================================
   COMPONENT: PROFESSIONAL INQUIRY SECTION
   Restrained contact section for serious inquiries only.
   NO purchase/bidding/auction/valuation language.
   ============================================================================ */
const InquirySection = () => (
  <section id="inquiry" style={{ padding: '80px 32px', background: 'white', borderTop: '1px solid #e8e6e2' }} data-testid="inquiry">
    <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
      <p style={{ marginBottom: '14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8B7355' }}>Professional Inquiries</p>
      <h2 style={{ margin: '0 0 20px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.3 }}>Research, Exhibition &amp; Media Inquiries</h2>
      <p style={{ maxWidth: '600px', margin: '0 auto 32px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', lineHeight: 1.75, color: '#555' }}>
        For serious inquiries from institutions, researchers, media professionals, or exhibition planners, please reach out with a detailed description of your interest.
      </p>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { type: 'Institutional', desc: 'Museums, universities, archives' },
          { type: 'Research', desc: 'Scholarly projects, publications' },
          { type: 'Exhibition', desc: 'Display loans, documentation' },
          { type: 'Media', desc: 'Documentary, editorial coverage' }
        ].map(item => (
          <div key={item.type} style={{ padding: '20px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
            <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{item.type}</p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888' }}>{item.desc}</p>
          </div>
        ))}
      </div>
      
      <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: '#666' }}>
        Contact: <a href="mailto:inquiries@elvisalohaarchive.com" style={{ color: '#8B7355', textDecoration: 'none', fontWeight: 500 }}>inquiries@elvisalohaarchive.com</a>
      </p>
      
      <p style={{ marginTop: '24px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
        All inquiries are reviewed for relevance. We prioritize scholarly, institutional, and legitimate media requests.
      </p>
    </div>
  </section>
);

/* ============================================================================
   COMPONENT: FOOTER
   ============================================================================ */
const Footer = () => (
  <footer role="contentinfo" style={{ padding: '64px 32px 40px', background: '#1a1a1a', color: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '48px', marginBottom: '40px' }}>
        <div style={{ maxWidth: '340px' }}>
          <p style={{ margin: '0 0 14px', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, letterSpacing: '0.06em' }}>ELVIS ALOHA COLLECTION</p>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)' }}>
            Preserving documented materials from Elvis Presley's January 1973 Aloha from Hawaii benefit concert.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A962' }}>Sections</p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} aria-label="Site sections">
              {['Inventory', 'Catalog', 'PSA Data', 'Registry', 'Production', 'Graceland', 'Gallery', 'About'].map(item => (
                <button key={item} onClick={() => scrollToSection(item === 'PSA Data' ? 'psa' : item.toLowerCase())} style={{ background: 'none', border: 'none', padding: '6px 0', minHeight: '32px', textAlign: 'left', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>{item}</button>
              ))}
            </nav>
          </div>
          <div>
            <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A962' }}>Legal</p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a href="/rights" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Rights</a>
              <a href="/privacy" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy</a>
              <a href="/accessibility" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Accessibility</a>
            </nav>
          </div>
        </div>
      </div>
      <div style={{ paddingTop: '28px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ margin: '0 0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', lineHeight: 1.7, color: 'rgba(255,255,255,0.4)' }}>
          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Independent Collection:</strong> Elvis Aloha Collection is an independent historical collection. Not affiliated with Elvis Presley Enterprises, Graceland, PSA, or any official Elvis Presley organization.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} Elvis Aloha Collection</p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   MAIN PAGE COMPONENT
   ============================================================================ */
const CompleteShowcasePage = () => {
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [lightboxState, setLightboxState] = useState({ isOpen: false, images: [], currentIndex: 0 });

  const openLightbox = useCallback((src, title, caption = '', category = '') => {
    setLightboxState({
      isOpen: true,
      images: [{ src, title, caption, category }],
      currentIndex: 0
    });
  }, []);

  const openGalleryLightbox = useCallback((startIndex) => {
    setLightboxState({
      isOpen: true,
      images: GALLERY_IMAGES,
      currentIndex: startIndex
    });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const nextImage = useCallback(() => {
    setLightboxState(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  }, []);

  const prevImage = useCallback(() => {
    setLightboxState(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  }, []);

  // Handle URL hash for deep linking
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#artifact-')) {
      const artifactId = hash.replace('#artifact-', '');
      const artifact = ARTIFACTS.find(a => a.id === artifactId);
      if (artifact) setSelectedArtifact(artifact);
    }
  }, []);

  return (
    <div data-testid="complete-showcase" style={{ background: 'white' }}>
      <Helmet>
        <title>Elvis Aloha Collection | Aloha from Hawaii 1973 Ticket Archive</title>
        <meta name="description" content="A documented historical archive of PSA-certified seat-numbered tickets, production materials, and artifacts from Elvis Presley's January 1973 Aloha from Hawaii benefit concert." />
        <link rel="canonical" href={`${window.location.origin}/`} />
        <meta property="og:title" content="Elvis Aloha Collection" />
        <meta property="og:description" content="A documented historical archive connected to Elvis Presley's January 1973 Aloha from Hawaii benefit concert." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
      </Helmet>
      <HeroSection />
      <InventorySection />
      <CatalogSection 
        onArtifactSelect={setSelectedArtifact} 
        onImageClick={openLightbox} 
      />
      <SeatingChartSection />
      <PSASection />
      <KnownTicketsRegistrySection />
      <ProductionSection onImageClick={openLightbox} />
      <GracelandSection onImageClick={openLightbox} />
      <GallerySection onImageClick={(src, title, caption, category) => {
        const idx = GALLERY_IMAGES.findIndex(img => img.src === src);
        if (idx >= 0) openGalleryLightbox(idx);
        else openLightbox(src, title, caption, category);
      }} />
      <AboutSection />
      <InquirySection />

      {/* Lightbox */}
      <Lightbox
        images={lightboxState.images}
        currentIndex={lightboxState.currentIndex}
        isOpen={lightboxState.isOpen}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />

      {/* Artifact Panel */}
      <ArtifactPanel
        artifact={selectedArtifact}
        isOpen={!!selectedArtifact}
        onClose={() => setSelectedArtifact(null)}
      />
    </div>
  );
};

/* ============================================================================
   COMPONENT: MOBILE BOTTOM NAVIGATION
   Fixed 5-button bar for mobile devices
   ============================================================================ */
const MobileBottomNav = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const routes = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/collection', label: 'Collection', icon: Archive },
    { path: '/arena', label: 'Arena', icon: Map },
    { path: '/tickets', label: 'Tickets', icon: Ticket },
    { path: '/research', label: 'Research', icon: BookOpen }
  ];
  
  const isActive = (path) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };
  
  return (
    <>
      <style>{`
        .home-mobile-nav {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #171717;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding: 8px 0;
          padding-bottom: max(8px, env(safe-area-inset-bottom));
          z-index: 90;
        }
        
        .home-mobile-nav__item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 8px 4px;
          text-decoration: none;
          color: #888;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          transition: color 0.15s;
        }
        
        .home-mobile-nav__item--active,
        .home-mobile-nav__item:hover {
          color: #D85C4A;
        }
        
        @media (max-width: 900px) {
          .home-mobile-nav { display: flex !important; }
        }
      `}</style>
      <nav className="home-mobile-nav" aria-label="Site navigation" data-testid="home-mobile-nav">
        {routes.map(route => {
          const Icon = route.icon;
          const active = isActive(route.path);
          return (
            <Link
              key={route.path}
              to={route.path}
              className={`home-mobile-nav__item ${active ? 'home-mobile-nav__item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={22} />
              <span>{route.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default CompleteShowcasePage;
